/**
 * CharSelect.tsx — R23-GH portrait sprites
 *
 * 4-seat archetype picker. Each seat is a row; each row shows 4 archetype
 * options as selectable tiles. Tap an archetype to assign it to that seat.
 * Uniqueness is enforced via the store's mutual-swap setter: assigning an
 * archetype that another seat holds swaps them.
 *
 * gamePhase flow: "menu" → "char-select" → "playing"
 *
 * VISIBLE WHEN: store.gamePhase === "char-select"
 * TEST PATH: MainMenu → Start Game → CharSelect renders → tap archetypes →
 *            Begin → playing (intro cutscene fires)
 */

import type React from "react";
import { useEffect, useState } from "react";
import { preloadGameAssets } from "../../assets/preloadAssets.js";
import {
  SEAT_COLORS,
  type SeatIndex,
  seatSymbolFor,
  useGameStore,
} from "../../store/game-store.js";
import styles from "./CharSelect.module.css";

type Archetype = "engineer" | "scientist" | "activist" | "politician";

interface ArchetypeDef {
  key: Archetype;
  title: string;
  pro: string;
  con: string;
  color: string;
  /** R23-GH: portrait image path served from /sprites/players/ */
  image: string;
  /** Fallback emoji for alt text + screen-reader use */
  fallbackIcon: string;
}

const ARCHETYPES: Readonly<Record<Archetype, ArchetypeDef>> = {
  engineer: {
    key: "engineer",
    title: "Engineer",
    pro: "Build cost −2 tokens",
    con: "+1 pollution/turn per facility",
    color: "#e07b30",
    image: "/sprites/players/engineer-portrait.png",
    fallbackIcon: "🔧",
  },
  scientist: {
    key: "scientist",
    title: "Scientist",
    pro: "Renewables +1 energy/turn",
    con: "+1 pollution/turn (research)",
    color: "#3a8fd1",
    image: "/sprites/players/scientist-portrait.png",
    fallbackIcon: "🔬",
  },
  activist: {
    key: "activist",
    title: "Activist",
    pro: "Facility pollution × 0.7",
    con: "Build cost +2 tokens",
    color: "#4caf50",
    image: "/sprites/players/activist-portrait.png",
    fallbackIcon: "🌱",
  },
  politician: {
    key: "politician",
    title: "Politician",
    pro: "Toll income × 1.5",
    con: "Trade ×0.5, Bail ×1.5, Decom 25%",
    color: "#9c27b0",
    image: "/sprites/players/politician-portrait.png",
    fallbackIcon: "🏛️",
  },
};

/** R23-GH: preload all 5 frames for a given archetype to avoid first-frame pop. */
function preloadArchetypeFrames(archetype: Archetype): void {
  const urls = [
    `/sprites/players/${archetype}-portrait.png`,
    `/sprites/players/${archetype}-idle-0.png`,
    `/sprites/players/${archetype}-idle-1.png`,
    `/sprites/players/${archetype}-walk-0.png`,
    `/sprites/players/${archetype}-walk-1.png`,
  ];
  for (const url of urls) {
    const img = new Image();
    img.src = url;
  }
}

const ARCHETYPE_ORDER: readonly Archetype[] = [
  "engineer",
  "scientist",
  "activist",
  "politician",
] as const;

// R15 carry-forward (R14 /goal MED): module-scope Set to avoid per-render alloc.
const VALID_ARCHETYPES: ReadonlySet<Archetype> = new Set(ARCHETYPE_ORDER);

const SEAT_LABELS = ["Player 1", "Player 2", "Player 3", "Player 4"] as const;

interface SeatRowProps {
  seat: SeatIndex;
  current: Archetype;
  onPick: (archetype: Archetype) => void;
}

function SeatRow({ seat, current, onPick }: SeatRowProps): React.ReactElement {
  const seatColor = SEAT_COLORS[seat] ?? "#fff";
  return (
    <div className={styles.seatRow} data-testid={`char-select-seat-row-${seat}`}>
      <div
        className={styles.seatHeader}
        style={{ borderLeftColor: seatColor }}
        data-testid={`char-select-seat-header-${seat}`}
      >
        <span
          className={styles.seatPip}
          style={{ backgroundColor: seatColor }}
          aria-hidden="true"
        />
        {/* R15 Builder-AW — colorblind seat symbol (aria-hidden; visual only) */}
        <span aria-hidden="true" style={{ color: seatColor, fontSize: "0.9em", marginRight: 2 }}>
          {seatSymbolFor(seat)}
        </span>
        <span className={styles.seatName} style={{ color: seatColor }}>
          {SEAT_LABELS[seat]}
        </span>
      </div>
      <div className={styles.optionGrid}>
        {ARCHETYPE_ORDER.map((key) => {
          const def = ARCHETYPES[key];
          const isSelected = current === key;
          return (
            <button
              key={key}
              type="button"
              className={`${styles.optionCard} ${isSelected ? styles.optionCardSelected : ""}`}
              style={{ borderColor: isSelected ? def.color : "transparent" }}
              onClick={() => onPick(key)}
              data-testid={`char-select-option-${seat}-${key}`}
              data-selected={isSelected ? "true" : "false"}
              aria-pressed={isSelected}
              aria-label={`Seat ${seat + 1}: ${def.title}`}
            >
              {/* R23-GH: portrait image (pixel-art, 80×80). Falls back to emoji alt text. */}
              <div className={styles.optionIcon} aria-hidden="true">
                <img
                  src={def.image}
                  alt={def.fallbackIcon}
                  className={styles.optionPortrait}
                  data-testid={`char-select-portrait-${key}`}
                  onError={(e) => {
                    const el = e.currentTarget as HTMLImageElement;
                    el.style.display = "none";
                    const fb = el.nextElementSibling as HTMLElement | null;
                    if (fb) fb.style.display = "inline";
                  }}
                />
                <span className={styles.optionPortraitFallback} aria-hidden="true">
                  {def.fallbackIcon}
                </span>
              </div>
              <div className={styles.optionTitle} style={{ color: def.color }}>
                {def.title}
              </div>
              <p className={styles.optionPro}>
                <span className={styles.proIcon}>+</span> {def.pro}
              </p>
              <p className={styles.optionCon}>
                <span className={styles.conIcon}>−</span> {def.con}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CharSelect(): React.ReactElement | null {
  const gamePhase = useGameStore((s) => s.gamePhase);
  const playerPerks = useGameStore((s) => s.playerPerks);
  const setPlayerPerk = useGameStore((s) => s.setPlayerPerk);
  const beginGame = useGameStore((s) => s.beginGame);

  // R23-GH: preload all archetype sprite frames on mount to avoid first-frame pop.
  // FIX#3: also kick off background prefetch of all session assets (terrain BGs,
  // NPC sprites, BGM, intro cutscene) so CacheFirst caches are warm before game.
  useEffect(() => {
    for (const archetype of ARCHETYPE_ORDER) {
      preloadArchetypeFrames(archetype);
    }
    // Non-blocking: runs async in background, idempotent on repeat calls.
    preloadGameAssets();
  }, []); // mount-only

  if (gamePhase !== "char-select") return null;

  return (
    <CharSelectBody playerPerks={playerPerks} setPlayerPerk={setPlayerPerk} begin={beginGame} />
  );
}

/**
 * Inner body — split out so the `count` hook obeys the rules-of-hooks (the parent
 * early-returns null when not in char-select; hooks must not run conditionally).
 */
function CharSelectBody({
  playerPerks,
  setPlayerPerk,
  begin,
}: {
  playerPerks: readonly [Archetype, Archetype, Archetype, Archetype];
  setPlayerPerk: (seat: SeatIndex, archetype: Archetype) => void;
  begin: (numPlayers: 2 | 3 | 4) => void;
}): React.ReactElement {
  // Player-count selector (local UI state, DEFAULT 4). Only the first `count` seats
  // play; the rest are "not in game". beginGame(count) locks it into authoritative state.
  const [count, setCount] = useState<2 | 3 | 4>(4);

  // Uniqueness check — store's mutual-swap setter normally enforces it.
  // R14 Codex-A MED 2: also validate every entry is a real archetype (catches
  // undefined/garbage that Set.size === 4 would let through).
  // R15 carry-forward: VALID_ARCHETYPES hoisted to module scope (was per-render).
  // Player-count support: validate ONLY the first `count` perks (the seats in play);
  // count=4 → checks all four, byte-identical to the old `=== 4` behavior.
  const inPlay = playerPerks.slice(0, count);
  const allValid = inPlay.every((p) => VALID_ARCHETYPES.has(p));
  const uniqueCount = new Set(inPlay).size;
  const allUnique = allValid && uniqueCount === count;

  return (
    <div className={styles.overlay} data-testid="char-select">
      <h1 className={styles.heading}>Choose Your Archetype</h1>
      <p className={styles.subheading}>
        Each player picks a different archetype. Tapping a shared archetype swaps with whoever
        currently holds it.
      </p>

      {/* Player-count selector: 2 / 3 / 4 humans. */}
      <div className={styles.countRow} data-testid="char-select-count-row">
        <span className={styles.countLabel}>Players:</span>
        {([2, 3, 4] as const).map((n) => (
          <button
            key={n}
            type="button"
            className={`${styles.countBtn} ${count === n ? styles.countBtnSelected : ""}`}
            onClick={() => setCount(n)}
            data-testid={`char-select-count-${n}`}
            data-selected={count === n ? "true" : "false"}
            aria-pressed={count === n}
          >
            {n}
          </button>
        ))}
      </div>

      <div className={styles.seatStack} data-testid="char-select-seat-stack">
        {(([0, 1, 2, 3] as SeatIndex[]).slice(0, count) as SeatIndex[]).map((seat) => (
          <SeatRow
            key={seat}
            seat={seat}
            current={playerPerks[seat]}
            onPick={(archetype) => setPlayerPerk(seat, archetype)}
          />
        ))}
      </div>

      <button
        type="button"
        className={styles.beginBtn}
        onClick={() => begin(count)}
        data-testid="char-select-begin"
        disabled={!allUnique}
        title={allUnique ? undefined : `All ${count} players must pick different archetypes`}
      >
        Begin
      </button>
    </div>
  );
}
